/* eslint-disable @typescript-eslint/restrict-template-expressions */
/**
 * @author        nico@zumper.com (Nico Rattazzi)
 * @copyright     Copyright (c) 2017, Zumper
 * @description   Favorite Button Container
 */

import { checkLoggedIn, getUser } from '@zcode-ui/web-zumper-global-state'
import { LIST_ITEM_FAVORITE, RELATED_MM_FAVORITE } from '@zcode-ui/web-zumper-integrations'
import { AnalyticsC, type Origin, localStorageSingleton, trackGAEvent } from '@zcode/nr-utils'
import { getRouteProps } from '@zcode/nr/es/shared/packages/history-middleware'
import { hocCompose, memoMapDispatchToProps, withErrorBoundary } from '@zcode/react-utils'
import type { Listable, Pin, Property, RawPinInventorySearch, State } from '@zcode/state-type'
import React from 'react'
import { connect } from 'react-redux'
import { toggleEntityFavorite } from '../../actions/favorite.action'
import * as SignInC from '../../constants/sign_in'
import { checkFavoritesView, isEntityFavorited } from '../../inspectors'
import { logger } from '../../logger'
import { pushSignUpUrl } from '../../modules/account/thunks'
import { removeListItem } from '../../modules/favoritesView/actions/index'
import type { PageName } from '../../modules/mixpanel/helpers/types'
import { mixpanelFavorite } from '../../modules/mixpanel/thunks/mixpanelFavorite'
import { trackDetailEvent } from '../../modules/traktor/thunks/trackDetailEvent'
import { trackListItemEvent } from '../../modules/traktor/thunks/trackListItemEvent'
import type { Dispatch } from '../../modules/types'
import { Favorite } from './Favorite'

interface OwnProps {
  analyticChannel?:
    | typeof AnalyticsC.FULL_DETAIL
    | typeof AnalyticsC.GALLERY
    | typeof AnalyticsC.PERMANENT_ADDRESS
    | typeof AnalyticsC.RELATED_MULTI_MESSAGE
    | typeof Origin.FAVORITES.BUILDING
    | typeof Origin.FAVORITES.LISTING
  /**
   * It looks to me like `analyticsOrigin` should be required (we end up sending "undefined" in
   * an analytics call; see `toggleEntityFavorite`), but many places that use `FavoriteContainer` don't
   * provide it 🤔
   */
  analyticsOrigin?: string
  disabled?: boolean
  disableTracking?: boolean
  entity: Listable | Pin<RawPinInventorySearch> | Property | null | undefined
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
  onUnfavorite?: (entity: Listable | Pin<RawPinInventorySearch> | Property) => void
  pageName?: PageName
  parentName?: string
}

export const mapStateToProps = (state: State, ownProps: OwnProps) => {
  return {
    disabled: ownProps.disabled || !ownProps.entity,
    label: `add ${ownProps?.entity?.headerText || ''} to favorites`,
    isFavorite: isEntityFavorited(state, ownProps.entity),
  }
}

const mapDispatchToProps = (dispatch: Dispatch, ownProps: OwnProps) => {
  return {
    onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
      e?.stopPropagation()
      const {
        analyticChannel,
        analyticsOrigin,
        disableTracking,
        entity,
        onClick,
        pageName,
        parentName,
        onUnfavorite,
      } = ownProps

      if (!entity) {
        return
      }

      onClick?.(e)

      dispatch((_, getState) => {
        const state = getState()
        const isFavorite = isEntityFavorited(state, entity)
        const isLoggedIn = checkLoggedIn(state)
        const user = getUser(state)
        const IGNORED_CHANNELS = [
          AnalyticsC.FULL_DETAIL,
          AnalyticsC.PERMANENT_ADDRESS,
          AnalyticsC.GALLERY,
        ]
        const isIgnoredTraktorEvent = IGNORED_CHANNELS.includes(analyticChannel ?? '')

        dispatch(toggleEntityFavorite({ analyticsOrigin, entity, isFavorite, isLoggedIn, user }))

        if (isFavorite) {
          const isFavoritesView = checkFavoritesView(dispatch(getRouteProps()))
          if (isFavoritesView) {
            dispatch(removeListItem({ entity }))
          }
          // NOTE: We are allowing a custom onUnfavorite to be passed in from the
          // outside. Within the Favorite component, the onUnfavorite from
          // mapDispatchToProps takes precedence. Here we're grabbing the version from
          // ownProps and calling it too. Some components, like ListItemMapCard need
          // to take additional actions beyond removeItemFromFavoritesList.
          onUnfavorite?.(entity)
        } else {
          if (
            !isLoggedIn &&
            !localStorageSingleton.getx('hideFavoriteSignUp') &&
            !disableTracking
          ) {
            trackGAEvent(AnalyticsC.FAVORITE, 'Prompted Sign In', analyticsOrigin)
            dispatch(pushSignUpUrl({ referrer: SignInC.REFERRERS.FAVORITE }))
          }
          dispatch(mixpanelFavorite({ pageName, entity }))

          if (parentName === AnalyticsC.LIST_ITEM) {
            dispatch(trackListItemEvent({ entity, name: LIST_ITEM_FAVORITE }))
          } else if (analyticChannel === AnalyticsC.RELATED_MULTI_MESSAGE) {
            dispatch(trackDetailEvent({ entity, name: RELATED_MM_FAVORITE }))
          } else {
            if (!isIgnoredTraktorEvent) {
              logger.error(
                new Error(
                  `Lost traktor favorite event. analyticChannel: ${analyticChannel}, parentName: ${parentName}`
                )
              )
            }
          }

          if (analyticChannel) {
            trackGAEvent(analyticChannel, 'Favorite')
          }
        }
      })
    },
  }
}

export const memoMapFavoriteDispatchToProps = memoMapDispatchToProps(mapDispatchToProps, [
  'analyticChannel',
  'analyticsOrigin',
  'disableTracking',
  'entity',
  'onClick',
  'parentName',
  'onUnfavorite',
])

export const FavoriteContainer = hocCompose(
  connect(mapStateToProps, memoMapFavoriteDispatchToProps),
  withErrorBoundary({ displayName: 'Favorite' }),
  Favorite
)
